/**
 * Mode Stack Management
 *
 * Manages the modal state stack for input handling. Modes can be:
 * - Occluding (default): blocks events from reaching lower modes
 * - Overlay: passes events through if no handler matches
 */

import type { ModeEntry, ModeOptions, LogLevel } from './types';

export interface ModeStack {
	/** Get the current stack as an array of mode names */
	getStack(): string[];

	/** Check if a mode is anywhere in the stack */
	isMode(name: string): boolean;

	/** Get the top mode */
	getTopMode(): ModeEntry;

	/** Push a mode onto the stack */
	pushMode(name: string, options?: ModeOptions): void;

	/** Pop a mode and everything above it from the stack */
	popMode(name: string): void;

	/** Get full mode entries (includes overlay flag) */
	getEntries(): ModeEntry[];

	/** Set log level for warnings */
	setLogLevel(level: LogLevel): void;
}

export function createModeStack(logLevel: LogLevel = 'warn'): ModeStack {
	let level = logLevel;

	// Stack always starts with 'default' mode
	const stack: ModeEntry[] = [{ name: 'default', overlay: false }];

	function log(message: string): void {
		if (level !== 'silent') {
			console.warn(`[input] ${message}`);
		}
	}

	return {
		getStack(): string[] {
			return stack.map((e) => e.name);
		},

		isMode(name: string): boolean {
			return stack.some((e) => e.name === name);
		},

		getTopMode(): ModeEntry {
			return stack[stack.length - 1];
		},

		pushMode(name: string, options?: ModeOptions): void {
			stack.push({
				name,
				overlay: options?.overlay ?? false,
			});
		},

		popMode(name: string): void {
			// Find the mode in the stack
			const index = stack.findIndex((e) => e.name === name);

			if (index === -1) {
				log(`popMode: mode '${name}' not in stack`);
				return;
			}

			// Don't allow popping 'default'
			if (index === 0) {
				log(`popMode: cannot pop 'default' mode`);
				return;
			}

			const entry = stack[index];

			// If the mode is an overlay, just remove it (don't pop modes above)
			// This allows multiple overlay modes to coexist independently
			if (entry.overlay) {
				stack.splice(index, 1);
				return;
			}

			// For non-overlay (occluding) modes, pop everything from this mode upward
			stack.splice(index);
		},

		getEntries(): ModeEntry[] {
			return [...stack];
		},

		setLogLevel(newLevel: LogLevel): void {
			level = newLevel;
		},
	};
}
